// src/pages/Fixtures.js — inline "My Picks" on each fixture
import React, { useEffect, useMemo, useState, useContext } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AnalystDesk from "../components/AnalystDesk";
import API from "../api";
import PredictionSkeleton from "../components/PredictionSkeleton";
import Card from "../components/ui/Card";
import PageHeader from "../components/ui/PageHeader";
import PageShell from "../components/ui/PageShell";
import ScoringGuide from "../components/ScoringGuide";
import MatchRow, { PointsPill, pointsTone, shortTeamName } from "../components/ui/MatchRow";
import TeamRecentMatches from "../components/TeamRecentMatches";
import CompetitionFilterBar from "../components/CompetitionFilterBar";
import SegmentedTabs from "../components/SegmentedTabs";
import StatusPill from "../components/StatusPill";
import FixtureCloseCountdown from "../components/FixtureCloseCountdown";
import { usePageTitle } from "../hooks/usePageTitle";
import { getTeamLogo } from "../getTeamLogo";
import { UserContext } from "../context/UserContext";
import { isFixturePredictionOpen } from "../utils/fixtureDeadline";
import { oneMoreGoal, pointsFor, pointsReason } from "../utils/scorePoints";
import { formatUpdated, isOnNow, isScoreHot, latestSync } from "../utils/liveMatch";
import LiveNow from "../components/LiveNow";
import {
  COMPETITION_OPTIONS,
  filterCompetitionsByEnabled,
  getEnabledCompetitionIds,
  pickInitialCompetitionId,
} from "../constants/competitionPrefs";
import {
  WC_ACTIVE_SEASON,
  WC_STAGE_OPTIONS,
  getStoredWorldCupStageFilter,
  setStoredWorldCupStageFilter,
} from "../constants/worldCup";
import { CURRENT_SEASON } from "../constants/season";

const LOGO_DIRS = {
  premier_league: "england__premier_league",
  laliga: "spain__laliga",
  bundesliga: "germany__bundesliga",
  serie_a: "italy__serie_a",
};

const TEAM_ALIASES = [
  { re: /\bbrighton\b|\bbrighton & hove albion\b|\bbrighton and hove albion\b/, value: "brighton" },
  { re: /\bmanchester united\b|\bman united\b/, value: "manchester united" },
  { re: /\bmanchester city\b|\bman city\b/, value: "manchester city" },
  { re: /\bnewcastle united\b|\bnewcastle\b/, value: "newcastle united" },
  { re: /\bwolverhampton wanderers\b|\bwolves\b/, value: "wolves" },
  { re: /\bnottingham forest\b|\bforest\b/, value: "nottingham forest" },
  { re: /\bwest ham united\b|\bwest ham\b/, value: "west ham" },
  { re: /\btottenham hotspur\b|\btottenham\b|\bspurs\b/, value: "tottenham" },
  { re: /\baston villa\b/, value: "aston villa" },
  { re: /\bchelsea\b/, value: "chelsea" },
  { re: /\barse?nal\b/, value: "arsenal" },
  { re: /\bliverpool\b/, value: "liverpool" },
  { re: /\bleeds united\b|\bleeds\b/, value: "leeds united" },
  { re: /\bburnley\b/, value: "burnley" },
  { re: /\bbrentford\b/, value: "brentford" },
  { re: /\bsunderland\b/, value: "sunderland" },
  { re: /\bfulham\b/, value: "fulham" },
  { re: /\bcrystal palace\b/, value: "crystal palace" },
  { re: /\bbournemouth\b/, value: "bournemouth" },
  { re: /\beverton\b/, value: "everton" },
];

function normalizeTeam(team) {
  if (!team || typeof team !== "string") return "";
  const n = team.trim().toLowerCase();
  for (const a of TEAM_ALIASES) {
    if (a.re.test(n)) return a.value;
  }
  return n;
}

const normalizeCrestKey = (value) =>
  (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/\b(fc|afc|cf|sc|ud|cd|club|de|del|la|el|the)\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

function matchDayKey(m) {
  if (!m || !m.match_date) return "nodate";
  const d = new Date(m.match_date);
  if (Number.isNaN(d.getTime())) return "nodate";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const matchKey = (m) =>
  `${m?.competition || ""}|${matchDayKey(m)}|${normalizeTeam(m?.home_team)}|${normalizeTeam(m?.away_team)}`;

function dayLabel(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "Date TBC";
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(d) - startOf(new Date())) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", day: "numeric", month: "short" });
}

function groupByDay(list) {
  const groups = [];
  list.forEach((m) => {
    const label = dayLabel(m.match_date);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(m);
    else groups.push({ label, items: [m] });
  });
  return groups;
}

function formatLastSynced(date) {
  if (!date) return null;
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return date.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function Fixtures() {
  usePageTitle("Fixtures");
  const { user } = useContext(UserContext);

  const enabledIds = useMemo(
    () => getEnabledCompetitionIds(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.enabled_competitions, user?.id]
  );
  const visibleCompetitions = useMemo(
    () => filterCompetitionsByEnabled(COMPETITION_OPTIONS, enabledIds),
    [enabledIds]
  );

  const [searchParams] = useSearchParams();
  const [competition, setCompetition] = useState(() => pickInitialCompetitionId(enabledIds, "premier_league"));
  const [analyst, setAnalyst] = useState(false);
  const [wcStageFilter, setWcStageFilter] = useState(() => getStoredWorldCupStageFilter());
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("upcoming"); // upcoming | completed
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [search, setSearch] = useState("");
  const [completedVisibleCount, setCompletedVisibleCount] = useState(20);
  const [crestMap, setCrestMap] = useState({});
  const [myPreds, setMyPreds] = useState([]);

  useEffect(() => {
    if (!enabledIds.includes(competition)) {
      setCompetition(pickInitialCompetitionId(enabledIds, "premier_league"));
    }
  }, [enabledIds, competition]);

  useEffect(() => {
    if (competition === "world_cup") setStoredWorldCupStageFilter(wcStageFilter);
  }, [competition, wcStageFilter]);

  useEffect(() => {
    setActiveTab("upcoming");
    setShowAllUpcoming(false);
    setCompletedVisibleCount(20);
  }, [competition, wcStageFilter]);

  useEffect(() => {
    const q = searchParams.get("q");
    const comp = searchParams.get("competition");
    if (q) setSearch(q);
    if (comp && enabledIds.includes(comp)) setCompetition(comp);
  }, [searchParams, enabledIds]);

  useEffect(() => {
    if (activeTab === "completed") setCompletedVisibleCount(20);
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const load = async (initial) => {
      if (initial) {
        setLoading(true);
        setError("");
      }
      try {
        const fixtureParams = { competition, includePostponed: true };
        if (competition === "world_cup") {
          fixtureParams.tournament_season = WC_ACTIVE_SEASON;
          if (wcStageFilter) fixtureParams.stage = wcStageFilter;
        } else {
          fixtureParams.season = CURRENT_SEASON;
        }
        const response = await API.get("/gameweeks/all-fixtures", { params: fixtureParams });
        let data = Array.isArray(response?.data) ? response.data : [];
        data = data.filter((m) => !m.competition || m.competition === competition);
        if (cancelled) return;
        setFixtures(data);
        setError("");
        timer = setTimeout(load, isScoreHot(data) ? 60 * 1000 : 5 * 60 * 1000);
      } catch (err) {
        console.error("❌ Error fetching fixtures:", err);
        if (cancelled) return;
        if (initial) {
          setFixtures([]);
          setError(err?.response?.data?.message || "Failed to load fixtures.");
        }
        timer = setTimeout(load, 60 * 1000);
      } finally {
        if (initial && !cancelled) setLoading(false);
      }
    };

    load(true);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [competition, wcStageFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (competition === "ucl" || competition === "wildcard" || competition === "world_cup") {
          if (!cancelled) setCrestMap({});
          return;
        }
        const { data } = await API.get("/admin/live-league-table", { params: { competition } });
        const map = {};
        (Array.isArray(data) ? data : []).forEach((row) => {
          if (!row?.team_name || !row?.crest_url) return;
          map[normalizeCrestKey(row.team_name)] = row.crest_url;
        });
        if (!cancelled) setCrestMap(map);
      } catch (_) {
        if (!cancelled) setCrestMap({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competition]);

  // The server reads the user from the token.
  useEffect(() => {
    (async () => {
      try {
        if (fixtures.length === 0) {
          setMyPreds([]);
          return;
        }
        const { data } = await API.post("/predictions/by-fixture-ids", { fixture_ids: fixtures.map((f) => f.id) });
        setMyPreds(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn("⚠️ Could not load my predictions:", err);
        setMyPreds([]);
      }
    })();
  }, [fixtures]);

  const predMap = useMemo(() => Object.fromEntries(myPreds.map((p) => [p.fixture_id, p])), [myPreds]);

  const dedupedFixtures = useMemo(() => {
    if (!Array.isArray(fixtures) || fixtures.length === 0) return [];
    const byKey = new Map();
    const predSet = new Set(myPreds.map((p) => p.fixture_id));

    for (const f of fixtures) {
      const k = matchKey(f);
      const existing = byKey.get(k);
      if (!existing) {
        byKey.set(k, f);
        continue;
      }
      const hasScoresExisting = existing.home_score != null && existing.away_score != null;
      const hasScoresNew = f.home_score != null && f.away_score != null;
      const hasPredExisting = predSet.has(existing.id);
      const hasPredNew = predSet.has(f.id);

      if (!hasPredExisting && hasPredNew) {
        byKey.set(k, f);
        continue;
      }
      if (hasPredExisting && !hasPredNew) continue;
      if (!hasScoresExisting && hasScoresNew) {
        byKey.set(k, f);
        continue;
      }
      if (hasScoresExisting && !hasScoresNew) continue;

      const exSync = existing.last_sync_at ? new Date(existing.last_sync_at) : 0;
      const newSync = f.last_sync_at ? new Date(f.last_sync_at) : 0;
      if (newSync > exSync) byKey.set(k, f);
    }
    return Array.from(byKey.values());
  }, [fixtures, myPreds]);

  const lastSyncedAt = useMemo(() => {
    const times = dedupedFixtures
      .map((f) => f.last_sync_at)
      .filter(Boolean)
      .map((value) => new Date(value).getTime())
      .filter((value) => !Number.isNaN(value));
    if (!times.length) return null;
    return new Date(Math.max(...times));
  }, [dedupedFixtures]);
  const isSyncStale = lastSyncedAt && (Date.now() - lastSyncedAt.getTime()) / 3600000 > 36;

  const now = new Date();
  const passesSearch = (m) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (m.home_team || "").toLowerCase().includes(q) || (m.away_team || "").toLowerCase().includes(q);
  };

  const liveFixtures = dedupedFixtures
    .filter((m) => isOnNow(m, now.getTime()) && passesSearch(m))
    .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
  const allUpcoming = dedupedFixtures
    .filter((m) => {
      const st = (m.status || "").toLowerCase();
      const future = new Date(m.match_date) >= now;
      return future && (st === "scheduled" || st === "postponed") && !isOnNow(m, now.getTime()) && passesSearch(m);
    })
    .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
  const in30Days = (m) => new Date(m.match_date).getTime() - now.getTime() <= 30 * 86400000;
  const upcomingFixtures = showAllUpcoming ? allUpcoming : allUpcoming.filter(in30Days);
  const hiddenUpcoming = allUpcoming.length - upcomingFixtures.length;

  const completedFixtures = dedupedFixtures
    .filter((m) => {
      const st = (m.status || "").toLowerCase();
      const past = new Date(m.match_date) < now;
      const finished = st === "finished" || (st !== "live" && m.home_score != null && m.away_score != null);
      return past && finished && !isOnNow(m, now.getTime()) && passesSearch(m);
    })
    .sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
  const displayedCompleted = completedFixtures.slice(0, completedVisibleCount);
  const canLoadMoreCompleted = completedVisibleCount < completedFixtures.length;
  const filteredFixtures = activeTab === "upcoming" ? upcomingFixtures : displayedCompleted;

  const leagueDir = LOGO_DIRS[competition];
  const logoFor = (name, crestUrl) => {
    if (competition === "world_cup") return crestUrl || null;
    if (!leagueDir) return null;
    return getTeamLogo(name, leagueDir) || crestMap[normalizeCrestKey(name)] || null;
  };

  const renderRow = (m) => {
    const p = predMap[m.id];
    const hasPick = p && p.predicted_home_score != null && p.predicted_away_score != null;
    const pickLabel = hasPick ? `${p.predicted_home_score}–${p.predicted_away_score}` : null;
    const matchDate = new Date(m.match_date);
    const time = Number.isNaN(matchDate.getTime())
      ? "TBC"
      : matchDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const common = {
      home: { name: m.home_team, logo: logoFor(m.home_team, m.home_crest_url) },
      away: { name: m.away_team, logo: logoFor(m.away_team, m.away_crest_url) },
    };

    if (activeTab === "completed") {
      const points = hasPick
        ? p.points_awarded != null
          ? p.points_awarded
          : pointsFor(p.predicted_home_score, p.predicted_away_score, m.home_score, m.away_score)
        : null;
      const reason = pointsReason(points);
      return (
        <MatchRow
          key={m.id}
          {...common}
          competition={m.competition || competition}
          tone={hasPick ? pointsTone(points ?? 0) : undefined}
          meta={<span className="tabular-nums">FT</span>}
          metaRight={hasPick ? <PointsPill points={points ?? 0} /> : <span className="text-[12px]">No pick</span>}
          center={
            <div className="text-center">
              <p className="text-score font-bold tabular-nums text-ink">
                {m.home_score != null && m.away_score != null ? `${m.home_score}–${m.away_score}` : "–"}
              </p>
              {pickLabel && <p className="text-[11px] text-muted tabular-nums">you {pickLabel}</p>}
              {reason && <p className="text-[11px] font-medium text-muted">{reason}</p>}
            </div>
          }
        />
      );
    }

    const st = (m.status || "").toLowerCase();
    const isLive = st === "live";
    const showForm = competition !== "world_cup";
    const open = !m.postponed && (st === "scheduled" || st === "live") && isFixturePredictionOpen(m);
    const swing =
      isLive && hasPick ? oneMoreGoal(p.predicted_home_score, p.predicted_away_score, m.home_score, m.away_score) : null;
    const liveScore = isLive && m.home_score != null && m.away_score != null;

    return (
      <MatchRow
        key={m.id}
        {...common}
        competition={m.competition || competition}
        homeExtra={showForm ? <TeamRecentMatches teamName={m.home_team} competition={competition} /> : null}
        awayExtra={showForm ? <TeamRecentMatches teamName={m.away_team} competition={competition} /> : null}
        tone={isLive ? "live" : undefined}
        live={isLive}
        to={open ? `/predictions?competition=${competition}` : undefined}
        meta={<span className="tabular-nums text-gray-700 font-medium">{time}</span>}
        metaRight={
          m.postponed || isLive ? (
            <StatusPill status={m.status} postponed={m.postponed} live={isLive} matchDate={m.match_date} />
          ) : open ? (
            <FixtureCloseCountdown fixture={m} />
          ) : (
            <span className="text-[12px]">Locked</span>
          )
        }
        center={
          <div className="text-center">
            {liveScore ? (
              <>
                <p className="text-score font-bold tabular-nums text-ink">
                  {m.home_score}–{m.away_score}
                </p>
                {pickLabel && <p className="text-[11px] text-muted tabular-nums">you {pickLabel}</p>}
              </>
            ) : pickLabel ? (
              <>
                <p className="text-score font-bold tabular-nums text-ink">{pickLabel}</p>
                <p className="text-[11px] text-muted">your pick</p>
              </>
            ) : (
              <span className={`text-meta font-semibold ${open ? "text-brand" : "text-muted"}`}>
                {open ? "Pick" : "No pick"}
              </span>
            )}
          </div>
        }
        footer={
          swing && (
            <p className="text-center text-[12px] text-muted tabular-nums">
              Now {swing.now}
              <span> · {shortTeamName(m.home_team)} goal {swing.homeGoal}</span>
              <span> · {shortTeamName(m.away_team)} goal {swing.awayGoal}</span>
            </p>
          )
        }
      />
    );
  };

  const pickOf = (id) => {
    const p = predMap[id];
    return p && p.predicted_home_score != null && p.predicted_away_score != null ? p : null;
  };
  const openFixtures = dedupedFixtures.filter((m) => {
    const st = (m.status || "").toLowerCase();
    return !m.postponed && (st === "scheduled" || st === "live") && isFixturePredictionOpen(m);
  });
  const openPicked = openFixtures.filter((m) => pickOf(m.id)).length;
  const scoredPicks = dedupedFixtures
    .filter((m) => m.home_score != null && m.away_score != null)
    .map((m) => pickOf(m.id))
    .filter((p) => p && p.points_awarded != null);
  const resultFacts = [
    ["Points", scoredPicks.reduce((s, p) => s + (p.points_awarded || 0), 0)],
    ["Exact", scoredPicks.filter((p) => p.points_awarded === 5).length],
    [
      "Hit rate",
      scoredPicks.length
        ? `${Math.round((scoredPicks.filter((p) => p.points_awarded > 0).length / scoredPicks.length) * 100)}%`
        : "—",
    ],
  ];
  const competitionName = visibleCompetitions.find((c) => c.id === competition)?.label || "";

  const rail = (
    <div className="hidden lg:block space-y-4">
      <section className="rounded-xl border border-line bg-white shadow-sm overflow-hidden">
        <h2 className="px-4 py-3 border-b border-gray-100 text-[15px] font-semibold text-ink">
          Your picks{competitionName ? ` · ${competitionName}` : ""}
        </h2>
        <div className="p-4">
          {openFixtures.length === 0 ? (
            <p className="text-sm text-muted">No matches open for picks right now.</p>
          ) : (
            <>
              <p className="text-[22px] leading-7 font-bold tabular-nums text-ink">
                {openPicked} <span className="text-[15px] font-medium text-muted">of {openFixtures.length} open picked</span>
              </p>
              <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-brand rounded-full"
                  style={{ width: `${Math.round((openPicked / openFixtures.length) * 100)}%` }}
                />
              </div>
              {openPicked < openFixtures.length && (
                <Link
                  to={`/predictions?competition=${competition}`}
                  className="mt-3 inline-flex w-full items-center justify-center h-10 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover"
                >
                  Pick {openFixtures.length - openPicked} more
                </Link>
              )}
            </>
          )}
        </div>
        <dl className="grid grid-cols-3 border-t border-line">
          {resultFacts.map(([label, value], i) => (
            <div key={label} className={`px-4 py-3 ${i ? "border-l border-line" : ""}`}>
              <dt className="text-[12px] text-muted">{label}</dt>
              <dd className="text-[17px] font-bold tabular-nums text-ink">{value}</dd>
            </div>
          ))}
        </dl>
        <p className="px-4 pb-3 text-[12px] text-muted">From {scoredPicks.length} scored picks this season</p>
      </section>
      <ScoringGuide />
    </div>
  );

  return (
    <div className="min-h-screen bg-paper text-ink">
      <PageShell rail={rail} stickyRail>
        <div className="space-y-4">
        <PageHeader
          title="Fixtures"
          meta={lastSyncedAt ? `Scores synced ${formatLastSynced(lastSyncedAt)}` : null}
          actions={
            <Link
              to="/predictions"
              className="inline-flex items-center h-9 px-3 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover"
            >
              Predict
            </Link>
          }
        />
        {isSyncStale && (
          <p className="text-[12px] text-gray-700 bg-white border border-line rounded-lg px-3 py-2">
            Scores and fixtures sync Mon, Thu and Sat (~11:30 IST), so results can lag a day or two after busy weeks.
          </p>
        )}

        <CompetitionFilterBar
          items={visibleCompetitions.map((c) => ({ key: c.id, label: c.label }))}
          activeKey={competition}
          onChange={setCompetition}
        >
          {competition === "world_cup" && (
            <div>
              <p className="text-[12px] font-medium text-muted mb-1.5">World Cup {WC_ACTIVE_SEASON} stage</p>
              <SegmentedTabs
                items={WC_STAGE_OPTIONS.map((s) => ({ key: s.id, label: s.label }))}
                activeKey={wcStageFilter}
                onChange={setWcStageFilter}
              />
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <SegmentedTabs
                items={[
                  { key: "upcoming", label: "Upcoming" },
                  { key: "completed", label: "Results" },
                ]}
                activeKey={activeTab}
                onChange={setActiveTab}
              />
              <button
                type="button"
                aria-pressed={analyst}
                onClick={() => setAnalyst((on) => !on)}
                className="hidden lg:inline-flex h-10 items-center rounded-full border border-line bg-white px-3 text-sm font-semibold text-ink hover:border-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
              >
                {analyst ? "List" : "Analyst"}
              </button>
            </div>
            <label className="flex items-center gap-2 bg-white border border-line rounded-lg px-3 h-10 w-full sm:max-w-[260px] focus-within:border-brand">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-500 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search team"
                aria-label="Search team"
                className="outline-none text-sm text-ink placeholder:text-gray-400 w-full min-w-0 bg-transparent"
              />
            </label>
          </div>
        </CompetitionFilterBar>

        <LiveNow
          matches={liveFixtures}
          pickFor={(match) => pickOf(match.id)}
          logoFor={(match, side) =>
            logoFor(
              side === "home" ? match.home_team : match.away_team,
              side === "home" ? match.home_crest_url : match.away_crest_url
            )
          }
          metaFor={() => competitionName}
          updated={formatUpdated(latestSync(liveFixtures))}
        />

        {loading ? (
          <PredictionSkeleton rows={6} />
        ) : error ? (
          <Card>
            <p className="text-center text-red-700">{error}</p>
          </Card>
        ) : filteredFixtures.length === 0 && !(activeTab === "upcoming" && liveFixtures.length > 0) ? (
          <Card>
            <p className="text-center text-muted">
              {search.trim()
                ? "No matches for that team."
                : activeTab === "upcoming"
                  ? "No upcoming fixtures."
                  : "No results yet."}
            </p>
          </Card>
        ) : (
          <div className={analyst ? "space-y-6 lg:hidden" : "space-y-6"}>
            {groupByDay(filteredFixtures).map((group) => (
              <section key={group.label} aria-label={group.label}>
                <h2 className="mb-2 px-1 text-meta font-semibold text-gray-700">{group.label}</h2>
                <div className="space-y-2">{group.items.map(renderRow)}</div>
              </section>
            ))}

            {activeTab === "upcoming" && hiddenUpcoming > 0 && (
              <button
                onClick={() => setShowAllUpcoming(true)}
                className="w-full h-11 rounded-lg border border-line bg-white text-sm font-semibold text-gray-700 hover:border-gray-400"
              >
                Show {hiddenUpcoming} later {hiddenUpcoming === 1 ? "match" : "matches"}
              </button>
            )}
            {activeTab === "completed" && canLoadMoreCompleted && (
              <button
                onClick={() => setCompletedVisibleCount((c) => c + 20)}
                className="w-full h-11 rounded-lg border border-line bg-white text-sm font-semibold text-gray-700 hover:border-gray-400"
              >
                Load 20 more
              </button>
            )}
          </div>
        )}
        {analyst && !loading && !error && filteredFixtures.length > 0 && (
          <div className="hidden lg:block">
            <AnalystDesk fixtures={[...liveFixtures, ...filteredFixtures]} predMap={predMap} logoFor={logoFor} />
          </div>
        )}
        </div>
      </PageShell>
    </div>
  );
}
